import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import CaseStudyCard from "../components/CaseStudyCard";

// Sample case study image from Figma
const caseStudyImage =
  "http://localhost:3845/assets/eccdce146438359965ab618fbd6b20b27fe1ddfd.png";

/**
 * CaseStudiesPage Component
 * Displays all case studies in a professional layout from Figma design
 * Also handles individual case study detail view when ID is provided in URL
 * Fully scalable for adding new case studies in the future
 */
const CaseStudiesPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Case Studies Data - Easy to extend with new studies
  const [caseStudies] = useState([
    {
      id: 1,
      title: "Shanti Sarovar - Spiritual Retreat Success",
      description:
        "Shanti Sarovar is a unique implementation site, a serene, spiritual retreat and caregiving center that maintains a high-pedigree micro-herd of 10 cattle of different breeds. Unlike industrial dairy farms, this site prioritizes holistic animal welfare and spiritual sanctity. However, the challenge remained purely technical: manual observation was insufficient to maintain the precise health standards required for such a high-value, sensitive environment.",
      image: caseStudyImage,
      fullDescription:
        "Shanti Sarovar represents one of our most unique deployments. Located in a serene spiritual retreat center, this implementation demonstrates how technology can enhance rather than replace traditional values.\n\nChallenge:\nThe facility manages a small but highly valued micro-herd of 10 pedigree cattle. Each animal is significant, and their health directly impacts the center's operations and mission. Manual observation, while historically effective, proved insufficient for detecting early signs of health issues that could compromise the herd.\n\nSolution:\nWe deployed our IoT-powered cattle monitoring system with custom configurations for their unique needs. The system provided:\n- 24/7 health monitoring without intrusive technology\n- Real-time alerts for health anomalies\n- Detailed activity and behavior tracking\n- Integration with their existing vet partnership\n\nResults:\nWithin the first month, the system detected a subclinical mastitis case 2 days before visible symptoms would have appeared. Early intervention saved the animal and prevented potential spread. The farm now operates with greater confidence and reduced veterinary costs.",
      metrics: {
        durationMonths: 12,
        herdSize: 10,
        milkYieldIncrease: "15%",
        diseaseDetectionRate: "98%",
      },
    },
  ]);

  const handleCardClick = (caseStudyId) => {
    // Navigate to blogs page for detailed case study
    navigate(`/blogs/${caseStudyId}`);
  };

  // Get the selected case study if ID is provided
  const selectedCaseStudy = id
    ? caseStudies.find((cs) => cs.id === parseInt(id))
    : null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  // If a specific case study ID is provided, show detail view
  if (selectedCaseStudy) {
    return (
      <div className="bg-white min-h-screen">
        {/* Header with back button */}
        <section className="bg-gradient-to-b from-white to-gray-50 py-16 px-4 md:px-8 lg:px-16">
          <div className="max-w-4xl mx-auto">
            <motion.button
              whileHover={{ x: -4 }}
              onClick={() => navigate("/case-studies")}
              className="flex items-center gap-2 text-[#2aa366] hover:text-[#1a7a4f] transition-colors mb-8 font-semibold"
            >
              <ArrowLeft size={20} />
              Back to All Case Studies
            </motion.button>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {selectedCaseStudy.title}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {selectedCaseStudy.description}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="px-4 md:px-8 lg:px-16">
          <div className="max-w-4xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <img
                src={selectedCaseStudy.image}
                alt={selectedCaseStudy.title}
                className="w-full h-auto object-cover"
              />
            </motion.div>
          </div>
        </section>

        {/* Detailed Content */}
        <section className="px-4 md:px-8 lg:px-16 pb-16">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="prose prose-lg max-w-none mb-12"
            >
              <div className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
                {selectedCaseStudy.fullDescription}
              </div>
            </motion.div>

            {/* Metrics Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-gray-50 rounded-2xl p-8 mb-12"
            >
              <div className="text-center">
                <p className="text-3xl font-bold text-[#2aa366] mb-2">
                  {selectedCaseStudy.metrics.herdSize}
                </p>
                <p className="text-gray-600 font-medium">Cattle</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#0084d1] mb-2">
                  {selectedCaseStudy.metrics.durationMonths}
                </p>
                <p className="text-gray-600 font-medium">Months in Operation</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#2aa366] mb-2">
                  {selectedCaseStudy.metrics.milkYieldIncrease}
                </p>
                <p className="text-gray-600 font-medium">Milk Yield Increase</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#0084d1] mb-2">
                  {selectedCaseStudy.metrics.diseaseDetectionRate}
                </p>
                <p className="text-gray-600 font-medium">Detection Accuracy</p>
              </div>
            </motion.div>

            {/* Key Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-blue-50 rounded-2xl p-8 mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Key Benefits Achieved</h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="text-[#2aa366] mt-1 flex-shrink-0" />
                  <span className="text-gray-700 text-lg">
                    Real-time health monitoring and early disease detection
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#2aa366] mt-1 flex-shrink-0" />
                  <span className="text-gray-700 text-lg">
                    Reduced veterinary costs and intervention time
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#2aa366] mt-1 flex-shrink-0" />
                  <span className="text-gray-700 text-lg">
                    Improved herd productivity and milk yield
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#2aa366] mt-1 flex-shrink-0" />
                  <span className="text-gray-700 text-lg">
                    Better breeding efficiency and animal welfare
                  </span>
                </li>
              </ul>
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-gradient-to-r from-[#2aa366] to-[#0084d1] rounded-2xl p-12 text-center text-white"
            >
              <h2 className="text-3xl font-bold mb-4">
                Ready to Achieve Similar Results?
              </h2>
              <p className="text-lg mb-8 opacity-90">
                Transform your farm with Vanix AI-powered cattle intelligence
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate("/")}
                  className="px-8 py-3 bg-white text-[#2aa366] rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Book a Farm Assessment
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-[#2aa366] transition-colors"
                >
                  Download Brochure
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  // Default case: Show all case studies

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-20">
        <motion.div
          className="max-w-4xl mx-auto px-6 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-6">
            <div className="inline-block border border-[rgba(0,0,0,0.1)] rounded-xl px-4 py-2 mb-4">
              <p className="text-sm font-bold text-black uppercase tracking-wide">
                Success Stories
              </p>
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            We Strive for{" "}
            <span className="bg-gradient-to-r from-[#2aa366] to-[#0084d1] bg-clip-text text-transparent">
              Perfection
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            See how farms across India have transformed their operations with
            Vanix intelligence
          </p>
        </motion.div>
      </section>

      {/* Case Studies Grid */}
      <section className="bg-white py-16 px-4 md:px-8 lg:px-16">
        <motion.div
          className="max-w-7xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Main Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {caseStudies.map((caseStudy) => (
              <motion.div
                key={caseStudy.id}
                variants={itemVariants}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.3 }}
              >
                <CaseStudyCard
                  id={caseStudy.id}
                  image={caseStudy.image}
                  title={caseStudy.title}
                  description={caseStudy.description}
                  onCardClick={handleCardClick}
                />
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-12 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-4">
              Ready to Transform Your Farm?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Join 10,000+ farmers who are already making their farms smarter
              with Vanix
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/")}
                className="px-8 py-3 bg-[#2aa366] text-white rounded-lg font-semibold hover:bg-[#229856] transition-colors"
              >
                Book a Farm Assessment
              </button>
              <button
                onClick={() => navigate("/")}
                className="px-8 py-3 border-2 border-gray-300 text-black rounded-lg font-semibold hover:border-[#2aa366] transition-colors"
              >
                Download Brochure
              </button>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Adding More Case Studies Info */}
      <section className="bg-gray-50 py-16 px-4 md:px-8 lg:px-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">
            More Success Stories Coming Soon
          </h2>
          <p className="text-gray-600 text-lg">
            We're continuously documenting successful implementations. More case
            studies will be added as we help more farms achieve their goals.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
